import type { DeletedChapterDto, DeletedSubjectDto } from "@mmm/contracts";
import { useQuery } from "../../api/useQuery.js";
import { getDeletedRecords } from "../../api/queries.js";
import { fireCommand, restoreChapter, restoreSubject } from "../../api/commands.js";
import { ListRow } from "../../components/molecules/ListRow.js";
import { LoadingRows, QueryStatus } from "../../components/ui/LoadingPlaceholders.js";
import { PageTitle } from "../../components/ui/PageTitle.js";
import { Button, ColorDot, EmptyState, ErrorState, useLanguage, useT } from "@mmm/ui";
type DeletedRow =
  | { kind: "subject"; deletedAt: string; subject: DeletedSubjectDto }
  | { kind: "chapter"; deletedAt: string; chapter: DeletedChapterDto };

/**
 * The one place a soft-deleted subject or chapter can be found and brought
 * back (docs/04-record-lifecycle.md) — restoreSubject/restoreChapter exist
 * on the API but nothing else in the app ever calls them. Reachable from the
 * Settings modal's "Recently Deleted" link.
 *
 * Chapters whose parent subject is also deleted are omitted by the
 * `getDeletedRecords` query itself (see its doc comment) — restoring such a
 * chapter would 404 until its subject is restored first, so there is nothing
 * for this screen to special-case.
 */
export function DeletedItemsScreen() {
  const deleted = useQuery(getDeletedRecords, undefined);
  const t = useT();
  const { language } = useLanguage();
  const formatDeletedAt = (iso: string): string =>
    new Intl.DateTimeFormat(language, { month: "short", day: "numeric", year: "numeric" }).format(new Date(iso));

  const subjects = deleted.data?.subjects ?? [];
  const chapters = deleted.data?.chapters ?? [];
  const rows: DeletedRow[] = [
    ...subjects.map((subject): DeletedRow => ({ kind: "subject", deletedAt: subject.deletedAt, subject })),
    ...chapters.map((chapter): DeletedRow => ({ kind: "chapter", deletedAt: chapter.deletedAt, chapter })),
  ].sort((a, b) => b.deletedAt.localeCompare(a.deletedAt));

  const loaded = Boolean(deleted.data);
  const hasError = Boolean(deleted.error) && !deleted.waiting;

  return (
    <div className="mmm-dashboard-screen">
      <PageTitle title={t("settings.recentlyDeleted")} />
      <QueryStatus queries={[deleted]} />

      <section className="mmm-dashboard-panel">
        <div className="mmm-dashboard-panel-head">
          <div>
            <h2>{t("deleted.title")}</h2>
            <p>{loaded ? t("deleted.count", { count: rows.length }) : t("deleted.loading")}</p>
          </div>
        </div>
        <div className="mmm-dashboard-panel-body">
          {hasError ? (
            <ErrorState variant="list" title={t("deleted.loadFailed")} onRetry={deleted.retry} />
          ) : deleted.loading ? (
            <LoadingRows rows={3} />
          ) : rows.length === 0 ? (
            <EmptyState
              variant="list"
              title={t("deleted.emptyTitle")}
              message={t("deleted.emptyMessage")}
            />
          ) : (
            <div className="mmm-deleted-item-list">
              {rows.map((row) =>
                row.kind === "subject" ? (
                  <ListRow
                    key={`subject-${row.subject.id}`}
                    className="mmm-deleted-item-row"
                    leading={<ColorDot color={row.subject.color} />}
                    title={row.subject.title}
                    meta={t("deleted.subjectMeta", { date: formatDeletedAt(row.subject.deletedAt) })}
                    trailing={
                      <Button
                        variant="secondary"
                        onClick={() => fireCommand(restoreSubject({ subjectId: row.subject.id }))}
                      >
                        {t("deleted.restore")}
                      </Button>
                    }
                  />
                ) : (
                  <ListRow
                    key={`chapter-${row.chapter.id}`}
                    className="mmm-deleted-item-row"
                    title={row.chapter.title}
                    meta={t("deleted.chapterMeta", {
                      subjectTitle: row.chapter.subjectTitle,
                      date: formatDeletedAt(row.chapter.deletedAt),
                    })}
                    trailing={
                      <Button
                        variant="secondary"
                        onClick={() => fireCommand(restoreChapter({ chapterId: row.chapter.id }))}
                      >
                        {t("deleted.restore")}
                      </Button>
                    }
                  />
                )
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
